<script setup lang="ts">
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, ZhPageTitle } from '@zerohuman/ui'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { api, asListResponse, type Member, type Role, type ToolBindingRow } from '@/api'
import FirstRunHint from '@/components/FirstRunHint.vue'
import ListSearchField from '@/components/ListSearchField.vue'
import TeammateCard from '@/components/TeammateCard.vue'
import { useListState } from '@/composables/useListState'
import { useUiMode } from '@/composables/useUiMode'
import { memberLabel } from '@/lib/memberLabel'
import { toolLabel } from '@/lib/presentation'
import { type TeammateTools, toolsServing } from '@/lib/teammateTools'

/**
 * Inside Team, a teammate is one card (#1746): name, job, team, what they are working on and their tools, A to Z.
 * The job (the roles they hold) is edited on the card with the member page's own calls; everything else is edited
 * on the member page. Roles themselves are managed under Advanced.
 *
 * Three reads, each degrading only what it feeds: the members (the cards), the roles (the job picker: without it
 * the job chips are read-only), and the tool connections, read once for the whole page rather than once per card,
 * which fill every card's Tools field by the rule Add a teammate counts "still to connect" with.
 *
 * One "Add a teammate" button (#2131), in Standard and Advanced mode alike, and the first-run hint's action, open the
 * same choice in a kit Dialog: the Catalog (`/members/add`) or setting a teammate up by hand (`/members/new`).
 */
const router = useRouter()
const { mode } = useUiMode()
const members = ref<Member[]>([])
const loading = ref(true)
const error = ref<string | null>(null)
/** Every role, for the pickers; null until read, and for good when the read is refused. */
const roles = ref<Role[] | null>(null)
const bindings = ref<ToolBindingRow[] | null>(null)
const bindingsFailed = ref(false)
const search = ref('')
/** #633: the search comes back on the next visit. */
const list = useListState('members', { search: { ref: search } })
/** The teammates whose role change is under way, and why the last one failed, each on its own card. */
const pending = ref<Record<string, boolean>>({})
const cardErrors = ref<Record<string, string>>({})
/**
 * Bumped by every re-read of the list after a role change, so only the latest one lands: answers can come back in
 * any order, and an older one would put back roles a newer change has already taken away.
 */
let membersRead = 0

/** Loaded, no error, and nobody at all: the page has never had anything on it, not a search narrowed to zero. */
const noMembers = computed(() => !loading.value && !error.value && members.value.length === 0)

const sorted = computed(() =>
    [...members.value].sort((left, right) =>
        memberLabel(left).localeCompare(memberLabel(right), undefined, { sensitivity: 'base' }),
    ),
)

/** What a search reads on a teammate: what the card shows, plus their email and persona, which it does not. */
function searchText(member: Member): string {
    return [
        member.name,
        memberLabel(member),
        member.email,
        member.persona ?? '',
        ...member.roles.map(role => role.name),
        ...(member.teams ?? []).map(team => team.name),
        ...member.leadOf,
    ]
        .join('\n')
        .toLowerCase()
}

const query = computed(() => search.value.trim().toLowerCase())
const shown = computed(() =>
    query.value ? sorted.value.filter(member => searchText(member).includes(query.value)) : sorted.value,
)

/** Each teammate's Tools field, from the one read of the connections. */
const toolsByMember = computed(() => {
    const byMember = new Map<string, TeammateTools>()
    for (const member of members.value) {
        byMember.set(member.id, toolsOf(member))
    }
    return byMember
})

function toolsOf(member: Member): TeammateTools {
    if (bindingsFailed.value) return { status: 'failed' }
    if (!bindings.value) return { status: 'loading' }
    const teamIds = (member.teams ?? []).map(team => team.id)
    const labels = toolsServing(bindings.value, member.id, teamIds).map(binding =>
        toolLabel(binding.toolType, mode.value),
    )
    return { status: 'ready', labels: [...new Set(labels)].sort((left, right) => left.localeCompare(right)) }
}

async function loadMembers(): Promise<void> {
    try {
        members.value = await api.members()
    } catch {
        error.value = 'Could not load members.'
    } finally {
        loading.value = false
    }
}

async function loadRoles(): Promise<void> {
    try {
        roles.value = await api.roles()
    } catch {
        // Refused or failed: the job chips still show, read-only, with no picker.
        roles.value = null
    }
}

async function loadTools(): Promise<void> {
    try {
        bindings.value = await api.toolBindings()
    } catch {
        bindingsFailed.value = true
    }
}

onMounted(async () => {
    await Promise.all([loadMembers(), loadRoles(), loadTools()])
})

/** Whether the Add a teammate choice is open. */
const choosing = ref(false)
/**
 * What one one-row read of the Catalog said, so option 1 can say when it has no jobs yet: `unread` until the choice
 * first opens, `reading` while the read is out, `failed` when it fails. Only `empty` says anything, and option 1 is
 * offered and goes to the Catalog flow whatever the answer, since a job can still be described there.
 */
const catalogJobs = ref<'unread' | 'reading' | 'some' | 'empty' | 'failed'>('unread')

async function readCatalog(): Promise<void> {
    catalogJobs.value = 'reading'
    const oneJob = { page: 1, limit: 1 }
    try {
        const listed = asListResponse(await api.catalogRoles(oneJob), oneJob)
        catalogJobs.value = listed.data.length === 0 && !(listed.total > 0) ? 'empty' : 'some'
    } catch {
        catalogJobs.value = 'failed'
    }
}

function openChoice(): void {
    choosing.value = true
    if (catalogJobs.value === 'unread' || catalogJobs.value === 'failed') void readCatalog()
}

async function chooseWay(path: '/members/add' | '/members/new'): Promise<void> {
    choosing.value = false
    await router.push(path)
}

function without<T>(record: Record<string, T>, key: string): Record<string, T> {
    return Object.fromEntries(Object.entries(record).filter(([id]) => id !== key))
}

/** What a failed change says on its card: the reason too in Advanced, where machine codes are shown. */
function failureText(failure: string, caught: unknown): string {
    const reason = caught instanceof Error ? caught.message : ''
    return mode.value === 'advanced' && reason ? `${failure} (${reason}).` : `${failure}.`
}

/**
 * Make one role change for one teammate. A failure is said on their card only and changes nothing. A change made
 * shows on the card at once, and the list is read again, as the member page reads the member again, so the card
 * shows what the server now holds; if that read fails, the card keeps the change it already shows.
 */
async function changeRole(
    member: Member,
    failure: string,
    change: () => Promise<unknown>,
    apply: (row: Member) => Member,
): Promise<void> {
    pending.value = { ...pending.value, [member.id]: true }
    cardErrors.value = without(cardErrors.value, member.id)
    try {
        await change()
    } catch (caught) {
        cardErrors.value = { ...cardErrors.value, [member.id]: failureText(failure, caught) }
        return
    } finally {
        pending.value = without(pending.value, member.id)
    }
    members.value = members.value.map(row => (row.id === member.id ? apply(row) : row))
    const read = ++membersRead
    try {
        const fresh = await api.members()
        if (read === membersRead) members.value = fresh
    } catch {
        // The change is made and already on the card.
    }
}

async function assign(member: Member, roleId: string): Promise<void> {
    const role = roles.value?.find(candidate => candidate.id === roleId)
    await changeRole(
        member,
        'Could not assign role',
        () => api.assignRole(member.id, roleId),
        row => (role ? { ...row, roles: [...row.roles, role] } : row),
    )
}

async function remove(member: Member, roleId: string): Promise<void> {
    await changeRole(
        member,
        'Could not remove role',
        () => api.removeRole(member.id, roleId),
        row => ({ ...row, roles: row.roles.filter(role => role.id !== roleId) }),
    )
}
</script>

<template>
	<section class="w-full space-y-4 p-8">
		<div class="flex items-center justify-between gap-4">
			<ZhPageTitle>Members</ZhPageTitle>
			<!-- #2131: one way in, with a choice of the Catalog or setting up by hand; both keep their own URL. -->
			<Button type="button" data-testid="add-teammate-button" @click="openChoice">Add a teammate</Button>
		</div>
		<Dialog :open="choosing" @update:open="open => (choosing = open)">
			<DialogContent data-testid="add-teammate-choice">
				<DialogHeader>
					<DialogTitle>Add a teammate</DialogTitle>
					<DialogDescription>Start from a ready-made job, or set them up yourself.</DialogDescription>
				</DialogHeader>
				<div class="grid gap-3">
					<Button
						type="button"
						variant="outline"
						class="h-auto w-full flex-col items-start gap-1 py-3 text-left whitespace-normal"
						data-testid="choose-catalog"
						@click="chooseWay('/members/add')"
					>
						<span class="font-medium">Choose from the Catalog</span>
						<span class="text-muted-foreground text-sm font-normal">
							Pick a ready-made job; their tasks come with it, and you connect the tools they need.
						</span>
						<span v-if="catalogJobs === 'empty'" class="text-muted-foreground text-sm font-normal">
							The Catalog has no jobs yet. Use Set up manually, or describe the job there.
						</span>
					</Button>
					<Button
						type="button"
						variant="outline"
						class="h-auto w-full flex-col items-start gap-1 py-3 text-left whitespace-normal"
						data-testid="set-up-manually"
						@click="chooseWay('/members/new')"
					>
						<span class="font-medium">Set up manually</span>
						<span class="text-muted-foreground text-sm font-normal">
							Fill in their name and details yourself, then give them a job and a team.
						</span>
					</Button>
				</div>
			</DialogContent>
		</Dialog>
		<p class="text-muted-foreground text-sm">
			Each teammate on one card: their job, their team, what they are working on and their tools. Give or take away
			a job on the card; open a teammate to change anything else.
		</p>
		<p v-if="error" class="text-destructive text-sm">{{ error }}</p>
		<!-- Nobody yet: say who will appear here and offer the one action that adds them. -->
		<FirstRunHint
			v-if="noMembers"
			lead="No members yet. Your teammates appear here once you add them, with their roles and the teams they lead."
			:action="{ label: 'Add a teammate' }"
			@act="openChoice"
		/>
		<template v-else-if="members.length">
			<div class="flex flex-wrap items-center gap-3">
				<ListSearchField v-model="search" type="search" aria-label="Search teammates" placeholder="Search by name, job or team" class="max-w-sm" />
				<Button v-if="!list.isDefault.value" type="button" variant="outline" size="sm" @click="list.clear()">Clear filters</Button>
			</div>
			<!-- A search narrowed to nobody is not the first-run state: a bare line, with no action. -->
			<p v-if="!shown.length" class="text-muted-foreground text-sm">No teammates match “{{ search.trim() }}”.</p>
			<div v-else class="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
				<TeammateCard
					v-for="member in shown"
					:key="member.id"
					:member="member"
					:roles="roles"
					:tools="toolsByMember.get(member.id) ?? { status: 'loading' }"
					:busy="pending[member.id] === true"
					:error="cardErrors[member.id] ?? null"
					@assign="roleId => assign(member, roleId)"
					@remove="roleId => remove(member, roleId)"
				/>
			</div>
		</template>
		<p v-else-if="loading" class="text-muted-foreground text-sm">Loading teammates…</p>
	</section>
</template>
